import { translateCodon } from "@/lib/tool-kit/biology-genetics/genetic-code";
import { reverseComplement } from "@/lib/tool-kit/biology-genetics/iupac";

export interface Orf {
  /** +1, +2, +3 on the given strand; −1, −2, −3 on the reverse complement. */
  frame: number;
  strand: "+" | "-";
  /** 1-based inclusive coordinates on the input sequence (start ≤ end on both strands). */
  start: number;
  end: number;
  /** Length in nucleotides, the stop codon included when there is one. */
  nucleotides: number;
  /** Length in amino acids, the stop excluded. */
  aminoAcids: number;
  protein: string;
  /** True when the ORF ends in a stop codon. */
  complete: boolean;
}

export interface OrfOptions {
  /** Shortest ORF to report, in amino acids (stop excluded). */
  minAminoAcids: number;
  searchBothStrands: boolean;
  /** Also report ORFs that run to the end of the sequence without a stop codon. */
  includeOpenEnded: boolean;
}

interface Found {
  from: number;
  to: number;
  complete: boolean;
}

/** ORFs in one strand: ATG to the next in-frame stop, outermost start only. */
function scanStrand(
  sequence: string,
  offset: number,
  options: OrfOptions
): Found[] {
  const found: Found[] = [];
  let open = -1;
  let i = offset;
  for (; i + 3 <= sequence.length; i += 3) {
    const codon = sequence.slice(i, i + 3);
    if (open === -1) {
      if (codon === "ATG") {
        open = i;
      }
      continue;
    }
    if (translateCodon(codon) === "*") {
      if ((i - open) / 3 >= options.minAminoAcids) {
        found.push({ from: open, to: i + 3, complete: true });
      }
      open = -1;
    }
  }
  if (
    open !== -1 &&
    options.includeOpenEnded &&
    (i - open) / 3 >= options.minAminoAcids
  ) {
    found.push({ from: open, to: i, complete: false });
  }
  return found;
}

function proteinOf(
  sequence: string,
  from: number,
  to: number,
  complete: boolean
): string {
  const end = complete ? to - 3 : to;
  let protein = "";
  for (let i = from; i + 3 <= end; i += 3) {
    protein += translateCodon(sequence.slice(i, i + 3));
  }
  return protein;
}

function strandOrfs(
  sequence: string,
  strand: "+" | "-",
  total: number,
  options: OrfOptions
): Orf[] {
  const orfs: Orf[] = [];
  for (let offset = 0; offset < 3; offset++) {
    for (const hit of scanStrand(sequence, offset, options)) {
      const nucleotides = hit.to - hit.from;
      orfs.push({
        frame: strand === "+" ? offset + 1 : -(offset + 1),
        strand,
        start: strand === "+" ? hit.from + 1 : total - hit.to + 1,
        end: strand === "+" ? hit.to : total - hit.from,
        nucleotides,
        aminoAcids: hit.complete ? nucleotides / 3 - 1 : nucleotides / 3,
        protein: proteinOf(sequence, hit.from, hit.to, hit.complete),
        complete: hit.complete,
      });
    }
  }
  return orfs;
}

/** All ORFs, longest first. `sequence` is DNA (T, not U). */
export function findOrfs(sequence: string, options: OrfOptions): Orf[] {
  const orfs = strandOrfs(sequence, "+", sequence.length, options);
  if (options.searchBothStrands) {
    orfs.push(
      ...strandOrfs(reverseComplement(sequence), "-", sequence.length, options)
    );
  }
  return orfs.sort(
    (a, b) =>
      b.aminoAcids - a.aminoAcids || a.start - b.start || a.frame - b.frame
  );
}
